import { LESSON_META_COLOR } from "@/utils/lessonStructuredTitle";
import React from "react";
import { SizableText, XStack, YStack } from "tamagui";

const TITLE_BLOCK_VARIANTS = {
  card: {
    gap: 0,
    reserveMetaSpaceWhenMissing: true,
    mobile: {
      metaFontSize: 10,
      metaLineHeight: 16,
      titleFontSize: 12,
      titleLineCount: 1,
      titleLineHeight: 16,
    },
    desktop: {
      metaFontSize: 10,
      metaLineHeight: 14,
      titleFontSize: 14,
      titleLineCount: 1,
      titleLineHeight: 20,
    },
  },
  suggested: {
    gap: 0,
    reserveMetaSpaceWhenMissing: false,
    mobile: {
      metaFontSize: 10,
      metaLineHeight: 16,
      titleFontSize: 18,
      titleLineCount: 2,
      titleLineHeight: 24,
    },
    desktop: {
      metaFontSize: 10,
      metaLineHeight: 16,
      titleFontSize: 18,
      titleLineCount: 2,
      titleLineHeight: 24,
    },
  },
} as const;

type LessonStructuredTitleBlockVariant = keyof typeof TITLE_BLOCK_VARIANTS;

type LessonMetaLineProps = {
  metaLine: string;
  metaPrefix?: string | null;
  metaSuffix?: string | null;
  metaFontSize: number;
  metaLineHeight: number;
};

export function LessonMetaLine({
  metaLine,
  metaPrefix,
  metaSuffix,
  metaFontSize,
  metaLineHeight,
}: LessonMetaLineProps) {
  if (metaPrefix && metaSuffix) {
    return (
      <XStack alignItems="center" flexWrap="nowrap" width="100%">
        <SizableText
          fontSize={metaFontSize}
          fontWeight="600"
          color={LESSON_META_COLOR}
          numberOfLines={1}
          ellipsizeMode="tail"
          overflow="hidden"
          lineHeight={metaLineHeight}
          flexShrink={1}
          minWidth={0}
        >
          {metaPrefix}
        </SizableText>
        <SizableText
          fontSize={metaFontSize}
          fontWeight="600"
          color={LESSON_META_COLOR}
          lineHeight={metaLineHeight}
          flexShrink={0}
        >
          {` - ${metaSuffix}`}
        </SizableText>
      </XStack>
    );
  }

  return (
    <SizableText
      fontSize={metaFontSize}
      fontWeight="600"
      color={LESSON_META_COLOR}
      numberOfLines={1}
      ellipsizeMode="tail"
      overflow="hidden"
      lineHeight={metaLineHeight}
    >
      {metaLine}
    </SizableText>
  );
}

type LessonStructuredTitleBlockProps = {
  displayTitle: string;
  metaLine?: string | null;
  metaPrefix?: string | null;
  metaSuffix?: string | null;
  titleColor: string;
  variant: LessonStructuredTitleBlockVariant;
  useDesktopLayout?: boolean;
};

export function LessonStructuredTitleBlock({
  displayTitle,
  metaLine,
  metaPrefix,
  metaSuffix,
  titleColor,
  variant,
  useDesktopLayout = false,
}: LessonStructuredTitleBlockProps) {
  const variantConfig = TITLE_BLOCK_VARIANTS[variant];
  const variantStyles = variantConfig[useDesktopLayout ? "desktop" : "mobile"];
  const minHeight =
    (metaLine || variantConfig.reserveMetaSpaceWhenMissing
      ? variantStyles.metaLineHeight + variantConfig.gap
      : 0) +
    variantStyles.titleLineHeight * variantStyles.titleLineCount;

  return (
    <YStack minHeight={minHeight} justifyContent="center">
      {metaLine ? (
        <LessonMetaLine
          metaLine={metaLine}
          metaPrefix={metaPrefix}
          metaSuffix={metaSuffix}
          metaFontSize={variantStyles.metaFontSize}
          metaLineHeight={variantStyles.metaLineHeight}
        />
      ) : null}
      <SizableText
        fontSize={variantStyles.titleFontSize}
        fontWeight="600"
        color={titleColor}
        numberOfLines={variantStyles.titleLineCount}
        ellipsizeMode="tail"
        overflow="hidden"
        lineHeight={variantStyles.titleLineHeight}
      >
        {displayTitle}
      </SizableText>
    </YStack>
  );
}
